import {
  Path,
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  PASSWORD_HELPER_TEXT,
} from "../../../../static/Constants";
import { colors, customShadows } from "../../../../theme";
import TextField from "../../../Components/ui/inputs/CommonTextField";
import Button from "../../../Components/ui/inputs/CommonButton";
import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ShieldCheck, Mail } from "lucide-react";
import NavBar from "../../../Components/NavBar";
import Api, { apiError } from "../../../Service/Api";
import { useAccount } from "../../../Context/AccountContext";
import SuccessPopup from "../../../Components/SuccessPopup";

const titles = {
  login: ["Welcome back", "Sign in to your AutoBET account."],
  signup: ["Create your account", "One account for your games and wallet."],
  verify: [
    "Verify your email",
    "Enter the six-digit code from your email to confirm your account.",
  ],
  forgot: ["Forgot your password?", "We'll email you a code to reset it."],
  reset: ["Choose a new password", "Enter your email code and a new password."],
};

export default function AuthPage({ mode = "login" }) {
  const { signin, clearAccount, user, loading } = useAccount();
  const history = useHistory();
  const location = useLocation();
  const from = location.state?.from;
  const destination =
    typeof from === "string" &&
    /^\/wallet(?:\/(?:add-balance|transactions|approvals))?(?:\?page=\d+)?$/.test(
      from
    )
      ? from
      : "/";
  const [email, setEmail] = useState(location.state?.email || "");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(location.state?.message || "");
  const [cooldown, setCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Success!");
  const [successMessage, setSuccessMessage] = useState("");
  const redirectedRef = useRef(false);
  const submitting = useRef(false);
  const codeMode = mode === "verify" || mode === "reset";
  const needsPassword = mode !== "forgot" && mode !== "verify";
  const newPassword = mode === "signup" || mode === "reset";

  const handleSuccessClose = () => {
    setShowSuccess(false);
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      history.replace(Path.login, { message: successMessage });
    }
  };

  useEffect(() => {
    if (mode === "login" && user && !loading) history.replace(destination);
  }, [user, loading, mode, history, destination]);
  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError("");
    setMessage("");
    if (newPassword && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (mode === "login") {
        await signin(normalizedEmail, password);
        history.replace(destination);
      } else if (mode === "signup") {
        const { data } = await Api.post("/auth/signup", {
          name: name.trim(),
          email: normalizedEmail,
          password,
        });
        setPassword("");
        setConfirmPassword("");
        history.push(Path.verifyEmail, {
          email: normalizedEmail,
          message: data.message,
        });
      } else if (mode === "forgot") {
        const { data } = await Api.post("/auth/forgot-password", {
          email: normalizedEmail,
        });
        history.push(Path.resetPassword, {
          email: normalizedEmail,
          message: data.message,
        });
      } else if (mode === "verify") {
        const { data } = await Api.post("/auth/verify-email", {
          email: normalizedEmail,
          otp,
        });
        setOtp("");
        redirectedRef.current = false;
        setSuccessTitle("Email Verified!");
        setSuccessMessage(
          data?.message || "Email verified successfully! You can now sign in."
        );
        setShowSuccess(true);
      } else {
        const { data } = await Api.post("/auth/reset-password", {
          email: normalizedEmail,
          otp,
          password,
        });
        if (mode === "reset") clearAccount();
        setPassword("");
        setConfirmPassword("");
        setOtp("");
        redirectedRef.current = false;
        setSuccessTitle("Password Reset Successful!");
        setSuccessMessage(
          data?.message ||
            "Your password has been reset successfully. You can now sign in."
        );
        setShowSuccess(true);
      }
    } catch (failure) {
      if (failure.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        history.push(Path.verifyEmail, {
          email: email.trim().toLowerCase(),
          message:
            "Verify your email to continue. You can request a new code below.",
        });
      } else setError(apiError(failure));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const resend = async () => {
    if (submitting.current || cooldown) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await Api.post(
        mode === "verify"
          ? "/auth/resend-verification"
          : "/auth/forgot-password",
        { email: email.trim().toLowerCase() }
      );
      setMessage(data.message);
      setCooldown(60);
    } catch (failure) {
      setError(apiError(failure));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <NavBar />
      <Box
        component="main"
        sx={{
          minHeight: "80vh",
          px: 2,
          pt: "110px",
          pb: 8,
          background: colors.background.subtle,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 460,
            mx: "auto",
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: `1px solid ${colors.neutral.border}`,
            boxShadow: customShadows.card,
          }}
        >
          <Stack spacing={2.5}>
            <Box sx={{ color: colors.primary.main }}>
              {codeMode ? <Mail size={32} /> : <ShieldCheck size={32} />}
            </Box>
            <Box>
              <Typography
                component="h1"
                variant="h4"
                sx={{ fontWeight: 750, fontSize: 28 }}
              >
                {titles[mode][0]}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {titles[mode][1]}
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" role="alert">
                {error}
              </Alert>
            )}
            {message && (
              <Alert severity="success" role="status">
                {message}
              </Alert>
            )}
            <Box component="form" onSubmit={submit}>
              <Stack spacing={2}>
                {mode === "signup" && (
                  <TextField
                    size="medium"
                    label="Full name"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    inputProps={{ minLength: 2, maxLength: 80 }}
                    disabled={busy}
                  />
                )}
                <TextField
                  size="medium"
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  inputProps={{ maxLength: 254 }}
                  disabled={busy}
                />
                {codeMode && (
                  <TextField
                    size="medium"
                    label="Email verification code"
                    autoComplete="one-time-code"
                    required
                    value={otp}
                    onChange={(event) =>
                      setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]{6}",
                      maxLength: 6,
                    }}
                    helperText="Codes expire after 10 minutes."
                    disabled={busy}
                  />
                )}
                {needsPassword && (
                  <TextField
                    size="medium"
                    label={newPassword ? "Set password" : "Password"}
                    type="password"
                    autoComplete={
                      newPassword ? "new-password" : "current-password"
                    }
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    inputProps={{
                      minLength: newPassword ? MIN_PASSWORD_LENGTH : 1,
                      maxLength: MAX_PASSWORD_LENGTH,
                    }}
                    helperText={newPassword ? PASSWORD_HELPER_TEXT : undefined}
                    disabled={busy}
                  />
                )}
                {newPassword && (
                  <TextField
                    size="medium"
                    label="Confirm password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    inputProps={{
                      minLength: MIN_PASSWORD_LENGTH,
                      maxLength: MAX_PASSWORD_LENGTH,
                    }}
                    disabled={busy}
                  />
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy}
                  sx={{ py: 1.5, borderRadius: 2, textTransform: "none" }}
                >
                  {busy ? (
                    <CircularProgress
                      size={23}
                      color="inherit"
                      aria-label="Please wait"
                    />
                  ) : (
                    {
                      login: "Sign in",
                      signup: "Create account",
                      verify: "Verify email",
                      forgot: "Send reset code",
                      reset: "Reset password",
                    }[mode]
                  )}
                </Button>
                {codeMode && (
                  <Button
                    variant="text"
                    onClick={resend}
                    disabled={busy || cooldown > 0 || !email}
                    sx={{ textTransform: "none" }}
                  >
                    {cooldown ? `Resend in ${cooldown}s` : "Resend email code"}
                  </Button>
                )}
              </Stack>
            </Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ fontSize: 14 }}
            >
              {mode === "login" ? (
                <>
                  <Link to={Path.register}>Create account</Link>
                  <Link to={Path.forgotPassword}>Forgot password?</Link>
                </>
              ) : (
                <Link to={Path.login}>Back to sign in</Link>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Box>
      <SuccessPopup
        show={showSuccess}
        setShowModal={setShowSuccess}
        title={successTitle}
        message={successMessage}
        btnName="Go to Sign In"
        onConfirm={handleSuccessClose}
        onClose={handleSuccessClose}
      />
    </>
  );
}
